import store from 'store';
import { message } from 'antd';
import { IAudienceFilters, IListItem } from '../interface';
import { serializeContacts, serializeList } from '../utils';
import { updateContactAlreadyExistInListState } from '../actions';
import { AUTOMATION_LISTS } from 'constants/types/automationTypes';
import { AUDIENCE_PEOPLE, PEOPLE_AUDIENCE } from 'constants/actionTypes';
import { closeDetailProfile, closeOverlay } from 'modules/GlobalActions';
import { setListIdAction } from 'modules/Audience/Actions/listDetailsActions';
import {
  AUDIENCE_LISTS,
  AUDIENCE_LISTS_ADD,
  AUDIENCE_LISTS_DETAILS,
  AUDIENCE_LISTS_NON_PROFILED_CONTACTS,
  AUDIENCE_LISTS_PROFILED_CONTACTS,
  AUDIENCE_LISTS_PROFILED_CONTACTS_DELETE,
  AUDIENCE_LISTS_SEARCH,
  AUDIENCE_LISTS_DELETE,
  AUDIENCE_LISTS_NAME_EDIT,
  AUDIENCE_LISTS_BOTH,
} from 'constants/types';
import { getQueryListId, getQueryParam, objectToQueryParams } from 'utils';
import {
  getAudienceListsService,
  createNewListService,
  getAudienceListDetailsService,
  getAudienceListDetailsContactsService,
  deleteAudienceListContactsService,
  deleteListService,
  editListNameService,
  exportListDetailsService,
  cloneListService,
  createNewListInWorkflowService,
} from 'modules/Services/AudienceService';
import {
  clearPeopleSearch,
  getRecommendedPeople,
  resetRecommendedContacts,
  resetSelectAllRecommendedContactsAction,
} from 'modules/Campaigns/CampaignAssets/actions';

// set reset time after deletion or addition
const setResetTimeout = (type: string) => {
  setTimeout(() => store.dispatch({ type }), 3000);
};

export const setAudienceFilterToggle = () => {
  store.dispatch({
    type: PEOPLE_AUDIENCE.AUDIENCE_FILTER_TOGGLE,
    showFilterToggle: true,
  });
};

export const resetAudienceFilterToggle = () => {
  store.dispatch({
    type: PEOPLE_AUDIENCE.AUDIENCE_FILTER_TOGGLE,
    showFilterToggle: false,
  });
};

export const getAudienceLists = (params: any) => {
  const queryParams = objectToQueryParams(params);
  store.dispatch({
    type: AUDIENCE_LISTS.LOAD,
  });
  let offset = params.offset;
  getAudienceListsService(queryParams)
    .then((res) => {
      if (res.data) {
        if (!res?.data?.message) {
          const serializedData = serializeList(res.data.lists);
          store.dispatch({
            type: AUDIENCE_LISTS.SUCCESS,
            payload: {
              lists: [...serializedData],
              totalLists: res.data.total_lists === 0 && offset > 0 ? offset : res.data.total_lists,
            },
          });
        }
      }
    })
    .catch((err) => {
      console.log('Error in getAudienceListsService : ', err);
      // check added because if two request goes at the same time then the first request throw the
      // following error which changes the loading state in the redux due to which a wrong message was rendered.
      if (err?.message !== 'Operation canceled due to new request.') {
        store.dispatch({
          type: AUDIENCE_LISTS.FAIL,
        });
      }
    });
};

export const updateAudienceLists = (newLists: IListItem[]) => {
  const serializedData = serializeList(newLists);
  store.dispatch({
    type: AUDIENCE_LISTS.SUCCESS,
    payload: { lists: [...serializedData], import: true },
  });
};

export const cloneList = (listId: string, isSearched: boolean) => {
  store.dispatch({
    type: AUDIENCE_LISTS.CLONE_LOAD,
  });
  cloneListService(listId)
    .then((res) => {
      const data = res.data;
      if (data.success) {
        if (isSearched) {
          store.dispatch({
            type: AUDIENCE_LISTS.CLONE_SUCCESS_SEARCH,
            payload: { listData: data.list_data },
            listId: listId,
            isSearched: isSearched,
          });
        } else {
          store.dispatch({
            type: AUDIENCE_LISTS.CLONE_SUCCESS,
            payload: { listData: data.list_data },
            listId: listId,
          });
        }
      } else {
        store.dispatch({
          type: AUDIENCE_LISTS.CLONE_FAIL,
        });
      }
    })
    .catch((err) => {
      store.dispatch({
        type: AUDIENCE_LISTS.CLONE_FAIL,
      });
    });
};

export const createNewAudienceList = (
  cancelHandler: () => void,
  setDisableButtonInList: (value: boolean) => void,
  setAToggle: (value: boolean) => void,
  history: any,
  listName: string,
  contactIds: any,
  fromSimilar: boolean,
  contactSidebarId?: number,
  listId?: string,
  selectAllFlag?: boolean,
  excludedIds?: any,
  inList?: boolean,
  originalListId?: string,
  fromPeopleTab?: boolean,
  selectedOrganization?: string[],
  selectedDesignation?: string[],
  selectedPersonality?: string[],
  selectedStatus?: string[],
  searchTerm?: string,
  noRedirection?: boolean,
  showMessages?: boolean,
  fromGloabalListing?: boolean,
) => {
  createNewListService(
    listName,
    contactIds,
    listId,
    selectAllFlag,
    excludedIds,
    originalListId,
    fromPeopleTab,
    selectedOrganization,
    selectedDesignation,
    selectedPersonality,
    selectedStatus,
    searchTerm,
  )
    .then((res) => {
      if (res.data.success) {
        // if list is created successfully, append at the top of all lists array
        let newItem = res.data?.data[0]?.listDetail;
        store.dispatch({
          type: AUDIENCE_LISTS_ADD.SUCCESS,
          listItem: newItem,
          payload: {
            listItem: newItem,
            message: 'List created successfully.',
          },
          fromSimilar: fromSimilar,
        });
        if (fromSimilar) closeDetailProfile();
        // Success Toaster
        if (listName) {
          // Creating a new list or adding contact to a newly created list
          message.success({
            content: `List has been created successfully!`,
          });
        } else {
          // Adding contacts to an existing list
          message.success({
            content: `The contacts have been added successfully!`,
          });
        }
        store.dispatch({
          type: AUDIENCE_LISTS_ADD.NEWLY_CREATED_LIST_CHECK,
          newListCreated: true,
        });
        resetSelectAllRecommendedContactsAction();
        clearPeopleSearch(); // reset search field when closing the asider
        store.dispatch({ type: AUDIENCE_LISTS.NAMES_RESET });
      }
      if (res.data.message === 'List with that name already exists') {
        message.error({ content: res.data.message });
      }
      if (showMessages) {
        if (res.data.success) {
          // for showing message on person/contact detail sidebar when list is created from there
          store.dispatch({
            type: AUDIENCE_LISTS_ADD.DETAILS_SUCCESS_MESSAGE,
            flag: true,
          });
        }
      }
      if (inList) {
        if (res.data.success) {
          let listDetail = res.data?.data[0]?.listDetail;
          if (!noRedirection) {
            store.dispatch({
              type: AUDIENCE_LISTS_ADD.NEWLY_CREATED_LIST_ID,
              newlyCreatedListId: listDetail.listId,
            });
            resetSelectAllRecommendedContactsAction();
            clearPeopleSearch(); // reset search field when closing the asider
            if (fromPeopleTab) {
              closeOverlay();
              setAToggle(false);
              closeDetailProfile();
              resetAudienceListDetails(); // reset list details
              resetAudienceListDetailsPeopleSearch(); // reset list details people search
              cancelHandler(); // reset new list asider
              setDisableButtonInList(false); // reset save button
              // when coming from people tab, we need to redirect to list view page in the last so that the components donot unmout before state change
              history.push(`/audiences/view?list_id=${listDetail.listId}`);
              setListIdAction(listDetail.listId);
            } else {
              history.push(`/audiences/view?list_id=${listDetail.listId}`);
              setListIdAction(listDetail.listId);
              closeOverlay();
              setAToggle(false);
              closeDetailProfile();
              resetAudienceListDetails(); // reset list details
              resetAudienceListDetailsPeopleSearch(); // reset list details people search
              cancelHandler(); // reset new list asider
              setDisableButtonInList(false); // reset save button
            }
          }
        } else {
          if (res.data.message === 'List with that name already exists') {
            store.dispatch({
              type: AUDIENCE_LISTS.FAIL,
              payload: true,
            });
          } else {
            store.dispatch({
              type: AUDIENCE_LISTS.FAIL,
            });
          }
        }
      } else if (fromSimilar && !fromPeopleTab) {
        if (res.data.success) {
          let listDetail = res.data?.data[0]?.listDetail;
          history.push(`/audiences/view?list_id=${listDetail.listId}`);
          store.dispatch({
            type: AUDIENCE_LISTS_ADD.NEWLY_CREATED_LIST_ID,
            newlyCreatedListId: listDetail.listId,
          });
          closeOverlay();
          setAToggle(false);
          closeDetailProfile();
          resetAudienceListDetails(); // reset list details
          resetAudienceListDetailsPeopleSearch(); // reset list details people search
        } else {
          if (res.data.message === 'List with that name already exists') {
            store.dispatch({
              type: AUDIENCE_LISTS.FAIL,
              payload: true,
            });
          } else {
            store.dispatch({
              type: AUDIENCE_LISTS.FAIL,
            });
          }
        }
      } else {
        if (res.data.success) {
          let listDetail = res.data?.data[0]?.listDetail;
          history.push(`/audiences/view?list_id=${listDetail.listId}`);
          store.dispatch({ type: AUDIENCE_LISTS_ADD.RESET });
          closeOverlay();
        } else {
          store.dispatch({
            type: AUDIENCE_LISTS_ADD.FAIL,
            message: res.data.message,
          });
          setTimeout(() => {
            store.dispatch({ type: AUDIENCE_LISTS_ADD.RESET });
          }, 5000);
        }
        store.dispatch({
          type: AUDIENCE_LISTS_ADD.NEWLY_CREATED_LIST_CHECK,
          newListCreated: false,
        });
      }
      store.dispatch({ type: AUDIENCE_LISTS.RESET });
    })
    .catch((err) => {
      console.log(err);
      store.dispatch({
        type: AUDIENCE_LISTS_ADD.FAIL,
        message: 'Something went wrong!',
      });
      setTimeout(() => {
        store.dispatch({ type: AUDIENCE_LISTS_ADD.RESET });
      }, 5000);
    });
};

export const resetAlreadyExistListErrorMessage = () => {
  store.dispatch({
    type: AUDIENCE_LISTS.RESET_ALREADY_EXIST_ERROR,
  });
};

export const createNewListWithIdsForAnalytics = (contactIds: any, listName: string) => {
  return createNewListService(contactIds, listName);
};

export const editContactListName = (listId: string, newName: string) => {
  store.dispatch({
    type: AUDIENCE_LISTS_NAME_EDIT.LOAD,
  });

  editListNameService(listId, newName)
    .then((res) => {
      if (res?.data['success'] === true) {
        store.dispatch({
          type: AUDIENCE_LISTS_DETAILS.SET_NAME,
          listName: newName,
        });
        store.dispatch({
          type: AUDIENCE_LISTS_NAME_EDIT.SUCCESS,
          listId,
          listName: newName,
        });
      } else {
        if (res?.data['message'] !== '') {
          if (String(res?.data['message']).startsWith('List with name')) {
            // List already exist error message
            setListDetailsNameError('List already exists with that name.');
          } else {
            setListDetailsNameError(res?.data['message']);
          }
        } else {
          store.dispatch({
            type: AUDIENCE_LISTS_NAME_EDIT.FAIL,
          });
        }
      }
      setTimeout(() => {
        store.dispatch({ type: AUDIENCE_LISTS_NAME_EDIT.RESET });
      }, 1000);
    })
    .catch((err) => {
      console.log(err);
      store.dispatch({
        type: AUDIENCE_LISTS_NAME_EDIT.FAIL,
      });
    });
};

export const setAudienceListSearchTerm = (searchTerm: string) => {
  store.dispatch({
    type: AUDIENCE_LISTS_SEARCH.SET_SEARCH,
    searchTerm,
  });
};

export const resetAudienceListSearchTerm = () => {
  store.dispatch({
    type: AUDIENCE_LISTS_SEARCH.RESET,
  });
};

export const resetAudiencePeopleSearchTerm = (isProfiled?: boolean) => {
  store.dispatch({
    type: AUDIENCE_PEOPLE.RESET,
    payload: { profiled: isProfiled },
  });
};
export const closeExportPopup = () => {
  store.dispatch({ type: AUDIENCE_LISTS.SET_CLOSE });
};

export const resetAudienceFiltersForExport = () => {
  store.dispatch({ type: AUDIENCE_LISTS.FILTERS, filters: {} });
};

export const exportListDetails = (params: any, listId?: number) => {
  store.dispatch({
    type: AUDIENCE_LISTS.ENABLE_EXPORT_LOADER,
  });
  exportListDetailsService(params, listId)
    .then((res) => {
      if (res) {
        store.dispatch({
          type: AUDIENCE_LISTS.SET_OPEN,
        });
        store.dispatch({
          type: AUDIENCE_LISTS.DISABLE_EXPORT_LOADER,
        });
      }
    })
    .catch((err) => {
      console.log('Error in exportListDetailsService : ', err);
      store.dispatch({
        type: AUDIENCE_LISTS_SEARCH.FAIL,
      });
    });
};
export const getAudienceSearchedLists = (params: any) => {
  const queryParams = objectToQueryParams(params);
  store.dispatch({
    type: AUDIENCE_LISTS_SEARCH.LOAD,
  });
  let offset = params.offset;
  getAudienceListsService(queryParams)
    .then((res) => {
      if (res.data) {
        if (!res.data.message) {
          const serializedData = serializeList(res.data.lists);
          store.dispatch({
            type: AUDIENCE_LISTS_SEARCH.SUCCESS,
            payload: {
              lists: [...serializedData],
              totalLists: res.data.total_lists === 0 && offset > 0 ? offset : res.data.total_lists,
            },
          });
        } else {
          // α case in search
          store.dispatch({
            type: AUDIENCE_LISTS_SEARCH.ERROR_SEARCH_TERM,
            message: res.data.message,
            success: false,
          });
          store.dispatch({
            type: AUDIENCE_LISTS_SEARCH.ERROR_SEARCH_TERM_LOADING,
          });
        }
      }
    })
    .catch((err) => {
      console.log('Error in getSearchedAudienceListsService : ', err);
      if (err.message !== 'Operation canceled due to new request.')
        store.dispatch({
          type: AUDIENCE_LISTS_SEARCH.FAIL,
        });
    });
};

export const getAudienceListDetails = (cancelToken?: any) => {
  let listId = getQueryParam('list_id');
  let store_list_id = store.getState().audienceListDetails.listId;
  if (store.getState().audienceListDetails.newlyCreatedListId) {
    listId = store.getState().audienceListDetails.newlyCreatedListId;
  } else {
    listId = getQueryParam('list_id');
  }
  if (listId === store_list_id) return;
  store.dispatch({
    type: AUDIENCE_LISTS_DETAILS.LOAD,
    listId,
  });
  getAudienceListDetailsService(listId, cancelToken)
    .then((res) => {
      // console.log("getAudienceListDetailsService : ", res);
      if (res.data) {
        store.dispatch({
          type: AUDIENCE_LISTS_DETAILS.SUCCESS,
          listName: res.data.list_name,
          campaignsAndAssetsCount: res.data.campaigns_and_assets_count,
          listId,
        });
      }
    })
    .catch((err) => {
      console.log('Error getAudienceListDetailsService : ', err);
      // show a popup / message for failure
      store.dispatch({
        type: AUDIENCE_LISTS_DETAILS.FAIL,
      });
    });
};

export const deleteAudienceList = (listId: string, isSearched: boolean) => {
  store.dispatch({
    type: AUDIENCE_LISTS_DELETE.LOAD,
  });
  deleteListService(listId)
    .then((res) => {
      if (res.data.success) {
        store.dispatch({
          type: AUDIENCE_LISTS_DELETE.SUCCESS,
        });

        store.dispatch({
          type: AUDIENCE_LISTS.REMOVE_FROM_LIST,
          listId: listId,
          isSearched: isSearched,
        });
        // Success Toaster
        if (res.data.message === 'List deleted successfully') {
          message.success({
            content: `List has been deleted successfully`,
          });
        }

        setTimeout(() => {
          store.dispatch({
            type: AUDIENCE_LISTS_DELETE.RESET,
          });
        }, 3000);
      } else {
        store.dispatch({
          type: AUDIENCE_LISTS_DELETE.FAIL,
        });
        // Error Taoster
        message.error({
          content: 'Unable to delete list. Please try again later or contact support at support@xiqinc.com.',
        });
      }
    })
    .catch((err) => {
      console.log(err);
      store.dispatch({
        type: AUDIENCE_LISTS_DELETE.FAIL,
      });
    });
};

export const getAudienceListDetailProfiledContacts = (params: any, cancelToken?: any) => {
  store.dispatch({
    type: AUDIENCE_LISTS_PROFILED_CONTACTS.LOAD,
  });
  let list_id = getQueryParam('list_id');
  if (store.getState().audienceListDetails.newlyCreatedListId) {
    list_id = store.getState().audienceListDetails.newlyCreatedListId;
  }
  if (!list_id) {
    for (let index = 0; index < 5; index++) {
      list_id = getQueryParam('list_id');
      if (list_id) break;
    }
  }
  // const queryParams = objectToQueryParams(params);
  getAudienceListDetailsContactsService(list_id, params, cancelToken)
    .then((res) => {
      if (store.getState().audienceListDetails.newlyCreatedListId) {
        store.dispatch({
          type: AUDIENCE_LISTS_ADD.NEWLY_CREATED_LIST_ID,
          newlyCreatedListId: '',
        });
      }
      if (!res.data.message) {
        if (res.data.people_details_list) {
          let serializedContacts = serializeContacts(res.data.people_details_list);
          store.dispatch({
            type: AUDIENCE_LISTS_PROFILED_CONTACTS.SUCCESS,
            profiledList: serializedContacts,
            listId: list_id,
            // Total count from backend will be correct only when offset is 0.
            ...(params.offset === 0 && { totalContacts: res.data.total_contacts }),
            reset: params.offset === 0 ? true : false,
          });

          store.dispatch({
            type: PEOPLE_AUDIENCE.SET_CURRENT_ACTIVE_LIST,
            payload: '',
          });
        }
      } else {
        // α case in search
        store.dispatch({
          type: AUDIENCE_LISTS_PROFILED_CONTACTS.ERROR_SEARCH_TERM,
          message: res.data.message,
          success: false,
        });
        store.dispatch({
          type: AUDIENCE_LISTS_PROFILED_CONTACTS.ERROR_SEARCH_TERM_LOADING,
        });
      }
    })
    .catch((err) => {
      console.log('Error getAudienceListDetailsContactsService:', err);
      if (err.message !== 'Operation canceled due to new request.') {
        store.dispatch({ type: AUDIENCE_LISTS_PROFILED_CONTACTS.FAIL });
      }
    });
};

export const updateListContactsCount = (listId: any, updateCount: any, add: boolean = false, deleteAllFlag?: boolean, countToDelete?: number) => {
  store.dispatch({
    type: AUDIENCE_LISTS.UPDATE_CONTACTS_COUNT,
    updateContactCount: {
      listId,
      updateCount,
      add,
      deleteAllFlag,
      countToDelete,
    },
  });
};

export const deleteProfiledContactFromList = (
  contacts_ids: any[],
  deleteAll: boolean,
  listId?: any,
  inDetailProfile?: boolean,
  unselectedContactIds?: any[],
  countToDelete?: number, // this is the count to be minimized from the lists total count in case of delete all
  selectedOrganization?: string[],
  selectedDesignation?: string[],
  selectedPersonalityType?: string[],
  selectedStatus?: string[],
  searchTermList?: string,
  params?: { [key: string]: any },
  cancelToken?: any,
) => {
  store.dispatch({
    type: AUDIENCE_LISTS_PROFILED_CONTACTS_DELETE.LOAD,
  });
  let list_id = listId ? listId : getQueryParam('list_id');
  return deleteAudienceListContactsService(
    list_id,
    contacts_ids,
    deleteAll,
    unselectedContactIds,
    'profiled',
    selectedOrganization,
    selectedDesignation,
    selectedPersonalityType,
    selectedStatus,
    searchTermList,
  )
    .then((res) => {
      if (inDetailProfile) {
        if (listId === getQueryListId()) {
          store.dispatch({
            type: AUDIENCE_LISTS_PROFILED_CONTACTS_DELETE.UNSUBSCRIBE_LIST_SUCCESS,
            toBeDeleted: contacts_ids,
          });
        }
      } else {
        store.dispatch({
          type: AUDIENCE_LISTS_PROFILED_CONTACTS_DELETE.SUCCESS,
          toBeDeleted: contacts_ids,
        });
      }
      if (deleteAll) {
        store.dispatch({
          type: AUDIENCE_LISTS_DETAILS.TOTAL_COUNT_UPDATE_ON_DELETE_ALL_LIST,
          unselectedIds: unselectedContactIds?.length,
        });
      }

      if (params && cancelToken) {
        getAudienceListDetailProfiledContacts(params, cancelToken);
        updateContactAlreadyExistInListState(contacts_ids);
      }

      updateListContactsCount(list_id, contacts_ids.length, false, deleteAll, countToDelete);
      setResetTimeout(AUDIENCE_LISTS_PROFILED_CONTACTS_DELETE.RESET);
      resetRecommendedContacts();
      getRecommendedPeople();
      return res.status;
    })
    .catch((err) => {
      console.log('Error Profiled deleteAudienceListContactsService : ', err);
      store.dispatch({
        type: AUDIENCE_LISTS_PROFILED_CONTACTS_DELETE.FAIL,
      });
      setResetTimeout(AUDIENCE_LISTS_PROFILED_CONTACTS_DELETE.RESET);
    });
};

export const changeResetCount = () => {
  store.dispatch({ type: AUDIENCE_LISTS.CHANGE_SEARCH_COUNT });
};

export const resetAudienceListDetails = () => {
  store.dispatch({ type: AUDIENCE_LISTS_DETAILS.RESET });
};

export const resetAudienceListDetailsProfiled = () => {
  store.dispatch({ type: AUDIENCE_LISTS_PROFILED_CONTACTS.RESET });
};

export const resetAudienceListDetailsNonProfiled = () => {
  store.dispatch({ type: AUDIENCE_LISTS_NON_PROFILED_CONTACTS.RESET });
};

export const resetAudienceLists = () => {
  store.dispatch({ type: AUDIENCE_LISTS.RESET });
};

export const removeProfiledFromUnprofiled = () => {
  store.dispatch({ type: AUDIENCE_LISTS_NON_PROFILED_CONTACTS.REMOVE_PROFILED });
};

export const removeUnprofiledFromProfiled = () => {
  store.dispatch({ type: AUDIENCE_LISTS_PROFILED_CONTACTS.REMOVE_NON_PROFILED });
};

export const reloadListDetails = (status: boolean, tab?: string) => {
  store.dispatch({
    type: AUDIENCE_LISTS_DETAILS.SET_RELOAD_LIST,
    reloadList: status,
    isProfiled: tab,
  });
};

export const resetAudiencePeopleTabData = () => {
  store.dispatch({ type: PEOPLE_AUDIENCE.LISTS_LOAD_ALL_PEOPLE_RESET });
};

export const reloadAudienceContacts = (state: boolean) => {
  store.dispatch({ type: PEOPLE_AUDIENCE.SET_RELOAD, payload: state });
};

export const resetAudienceListDetailsPeopleSearch = () => {
  console.log('resetAudienceListDetailsPeopleSearch AUDIENCE_LISTS_DETAILS.SET_SEARCH_TERM');
  store.dispatch({
    type: AUDIENCE_LISTS_DETAILS.SET_SEARCH_TERM,
    searchTermListDetails: '',
  });
};

export const setAppliedSearchInListDetails = (appliedSearch: boolean) => {
  store.dispatch({
    type: AUDIENCE_LISTS_DETAILS.SET_APPLIED_SEARCH,
    appliedSearch,
  });
};

export const setClearSearchTermInListDetails = (clearSearchTerm: boolean) => {
  store.dispatch({
    type: AUDIENCE_LISTS_DETAILS.SET_CLEAR_SEARCH_TERM,
    clearSearchTerm,
  });
};

export const createNewListInWorkflow = (listName: string, isSearched: boolean, searchTerm: string) => {
  createNewListInWorkflowService(listName)
    .then(({ data }) => {
      if (data.success) {
        const {
          isCampaign,
          lastUpdated,
          listId,
          listName,
          thumbnails: topContactsThumbnails,
          topContactsPersonIds,
          totalContacts,
        } = data.data[0].listDetail;
        store.dispatch({
          type: AUTOMATION_LISTS.NEW_LIST_CREATED,
          newlyCreatedListInWorkflow: {
            is_camaign: isCampaign,
            list_id: listId,
            last_updated: lastUpdated,
            list_name: listName,
            thumbnails: topContactsThumbnails ? topContactsThumbnails : [],
            top_contacts_person_ids: topContactsPersonIds,
            total_contacts: totalContacts,
          },
          payload: isSearched && listName.includes(searchTerm),
        });
        store.dispatch({
          type: AUTOMATION_LISTS.CREATED_SUCCESS_STATE,
          payload: true,
        });
      } else {
        store.dispatch({
          type: AUTOMATION_LISTS.CREATED_ERROR_STATE,
          payload: true,
        });
      }
    })
    .catch((err) => {
      store.dispatch({
        type: AUTOMATION_LISTS.CREATED_ERROR_STATE,
        payload: true,
      });
    });
};

export const addToExistingList = (payload: [], count: number) => {
  store.dispatch({
    type: AUDIENCE_LISTS_BOTH.ADD_TO_EXISTING_LIST,
    contactsToAdd: payload,
    contactsAddCount: count,
  });
};

export const removeFromExistingList = (payload: []) => {
  store.dispatch({
    type: AUDIENCE_LISTS_BOTH.REMOVE_FROM_EXISTING_LIST,
    remainingProfilesInList: payload,
  });
};

export const applySelectedFiltersListDetails = (filters: IAudienceFilters) => {
  store.dispatch({
    type: AUDIENCE_LISTS_PROFILED_CONTACTS.SET_FILTERS,
    filters,
  });
};

export const resetApplySelectedFiltersListDetails = () => {
  let filters: IAudienceFilters = {
    designations: [],
    organizations: [],
    personality: [],
    status: [],
  };
  store.dispatch({
    type: AUDIENCE_LISTS_PROFILED_CONTACTS.SET_FILTERS,
    filters,
  });
};

export const setSelectedFiltersListDetails = (selectedFilters: IAudienceFilters) => {
  store.dispatch({
    type: AUDIENCE_LISTS_PROFILED_CONTACTS.SET_SELECTED_FILTERS,
    selectedFilters,
  });
};

export const resetSelectedFiltersListDetails = () => {
  let selectedFilters: IAudienceFilters = {
    designations: [],
    organizations: [],
    personality: [],
    status: [],
  };
  store.dispatch({
    type: AUDIENCE_LISTS_PROFILED_CONTACTS.SET_SELECTED_FILTERS,
    selectedFilters,
  });
};

export const setListDetailsNameError = (message: string) => {
  store.dispatch({
    type: AUDIENCE_LISTS_DETAILS.ALREADY_EXISTS,
    message,
  });
};

export const resetContactsAddedToList = () => {
  store.dispatch({
    type: AUDIENCE_LISTS_DETAILS.RESET_CONTACTS_ADDED_TO_LIST,
  });
};
